<template>
  <PopupContent :bodyClass="['mnemonic-popup-body']">
    <template #header>
      <div class="mnemonic-popup-header">
        <button class="pill-btn pill-btn--ghost" type="button" @click="viewModel.handleOk()">
          <span class="mnemonic-back-arrow" aria-hidden="true">←</span>
          <span>{{ $t('article.back') }}</span>
        </button>
        <h3 class="mnemonic-popup-title">
          {{ $t('mnemonicPopup.partWordsTitle', { part: viewModel.partIndex }) }}
        </h3>
      </div>
    </template>

    <div v-if="viewModel.items.length === 0" class="mnemonic-empty">
      {{ $t('mnemonicPopup.empty') }}
    </div>

    <WordCard
      v-for="item in viewModel.items"
      :key="item.wordId"
      :word="item.word"
      :translation="item.translation"
      :transliteration="item.transliteration"
      :normalized-word="item.normalizedWord"
      :normalized-translation="item.normalizedTranslation"
      :source-word-class="translatedWordClass"
      :translation-word-class="sourceWordClass"
      :translit-word-class="translitClass"
      :mnemonic-image-url="item.imageUrl"
      :mnemonic-text="item.mnemonicText"
      :saved="isWordSaved(item.wordId)"
      :saving="isWordSaving(item.wordId)"
      :show-like-buttons="true"
      :mnemonic-rating="getRating(item.wordId)"
      :mnemonic-like-pending="isPending(item.wordId)"
      :mnemonic-like-disabled="!isLikeAuthenticated"
      @toggle-saved="toggleWordCollection(item.wordId)"
      @mnemonic-like="(rating: number) => setRating(item.wordId, rating as 1 | -1)"
    />

    <template #footer>
      <div class="mnemonic-footer">
        <button class="pill-btn pill-btn--ghost mnemonic-close-button" @click="viewModel.handleOk()">
          {{ $t('common.close') }}
        </button>
      </div>
    </template>
  </PopupContent>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { IUserFsrsCardService, MnemonicPopupViewModel } from '@lingvo-news/contracts';
import PopupContent from './PopupContent.vue';
import WordCard from './WordCard.vue';
import { getTextClass } from '../styles/get-text-classes';
import { getVueEnv } from '../../infrastructure/vue-env';
import { getCurrentUserStore } from '../../store/services';
import { useWordStatsStore } from '../../store/word-stats/word-stats-store';
import { useWordBookmark } from '../../composables/useWordBookmark';
import { useMnemonicLikes } from '../../composables/useMnemonicLikes';

interface Props {
  readonly viewModel: MnemonicPopupViewModel;
}

const props = defineProps<Props>();
const translatedWordClass = computed(() => getTextClass(props.viewModel.translatedWordLanguage));
const sourceWordClass = computed(() => getTextClass(props.viewModel.sourceWordLanguage));
const translitClass = computed(() => getTextClass(props.viewModel.nativeLanguage));
const wordStatsStore = useWordStatsStore();

const resolveUserId = async (): Promise<string | null> => {
  const userStore = getCurrentUserStore();
  await userStore.initialize();
  return userStore.getCurrentUserId() ?? null;
};

const { isWordSaved, isWordSaving, markSavedBulk, toggle } = useWordBookmark({
  resolveUserId,
  async onUnauthenticated() {
    const env = getVueEnv();
    await env.routing.navigate(env.routing.getLoginLink());
  },
});

const mnemonicWordIds = computed(() => props.viewModel.items.map(item => item.wordId));
const { getRating, isPending, setRating, isAuthenticated: isLikeAuthenticated } = useMnemonicLikes(mnemonicWordIds, resolveUserId);

const loadSavedStates = async (): Promise<void> => {
  try {
    const targetWordIds = props.viewModel.items.map((item) => item.wordId);
    if (targetWordIds.length === 0) return;

    const userId = await resolveUserId();
    if (!userId) return;

    const env = getVueEnv();
    const wordsService = env.get(IUserFsrsCardService);

    const response = await wordsService.checkUserWords({ userId, translatedWordIds: targetWordIds });
    markSavedBulk(new Set(response.savedWordIds));
  } catch (error) {
    console.error('Failed to load saved word states', error);
  }
};

const toggleWordCollection = async (wordId: string): Promise<void> => {
  await toggle(wordId);
  await wordStatsStore.refreshStats();
};

onMounted(() => {
  void loadSavedStates();
});
</script>

<style scoped lang="less">
@import "../styles/text-styles.less";

.mnemonic-popup-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mnemonic-popup-title {
  margin: 0 auto 0 0;
  font-size: var(--font-size-subheader);
  color: #30373f;
}

.mnemonic-back-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

:deep(.mnemonic-popup-body) {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-size: var(--font-size-regular);
}

.mnemonic-empty {
  font-size: var(--font-size-regular);
  color: #5f6a74;
}


.mnemonic-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
}

.mnemonic-close-button {
  min-width: 340px;
}

@media (max-width: 768px) {
  :deep(.mnemonic-popup-body) {
    gap: 12px;
  }

  .mnemonic-footer {
    width: 100%;
  }

  .mnemonic-close-button {
    min-width: 150px;
  }
}
</style>
